import React, { useRef, useEffect } from 'react';
import { md, iconeType } from '../utils';

/* ── Indicateur typing ── */
function TypingBulle() {
  return (
    <div style={styles.rowBot}>
      <div style={styles.auteur}>Assistant 3F</div>
      <div style={styles.typing}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{ ...styles.dot, animationDelay: `${i * 0.2}s` }} />
        ))}
      </div>
    </div>
  );
}

/* ── Bulle message ── */
function Bulle({ msg, nomAssistant }) {
  const estUser = msg.role === 'user';

  return (
    <div style={{ ...styles.row, ...(estUser ? styles.rowUser : styles.rowBot) }}>
      {!estUser && <div style={styles.auteur}>{nomAssistant}</div>}
      <div style={{ ...styles.bulle, ...(estUser ? styles.bulleUser : styles.bulleBot) }}>

        {/* Texte avec markdown */}
        {msg.texte && (
          <div
            style={styles.texte}
            dangerouslySetInnerHTML={{ __html: md(msg.texte) }}
          />
        )}

        {/* Pièces jointes */}
        {msg.pjs?.length > 0 && (
          <div style={styles.pjListe}>
            {msg.pjs.map((f, i) => (
              <div key={i} style={styles.pjItem}>
                <span>{iconeType(f.type)}</span>
                <span style={styles.pjNom} title={f.nom}>{f.nom}</span>
                <span style={styles.pjSz}>{f.sz}</span>
              </div>
            ))}
          </div>
        )}

        {/* Bouton document généré par n8n */}
        {!estUser && msg.data && (msg.data.document_url || msg.data.pdf_url) && (
          <button
            style={styles.btnDoc}
            onClick={() => window.open(msg.data.document_url || msg.data.pdf_url, '_blank')}
          >
            📄 Ouvrir le document généré
          </button>
        )}

        {/* Heure */}
        <div style={{ ...styles.heure, ...(estUser ? styles.heureUser : {}) }}>
          {msg.heure}
          {estUser && <span style={styles.coches}>✓✓</span>}
        </div>
      </div>
    </div>
  );
}

/* ── Séparateur de date ── */
function DateSep({ date }) {
  return (
    <div style={styles.dateSep}>
      <span style={styles.dateSepInner}>{date}</span>
    </div>
  );
}

/* ── Liste de messages ── */
export default function Messages({ messages, nomAssistant }) {
  const bas = useRef(null);
  let dernierDate = null;

  useEffect(() => {
    bas.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!messages.length) {
    return (
      <div style={styles.fond}>
        <div style={styles.vide}>
          <div style={styles.videEmoji}>💬</div>
          <div style={styles.videTitre}>Démarrez la conversation</div>
          <div style={styles.videSous}>Posez votre question ou choisissez une suggestion ci-dessus</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.fond}>
      {messages.map((msg) => {
        let sep = null;
        if (msg.date && msg.date !== dernierDate) {
          dernierDate = msg.date;
          sep = <DateSep key={'sep_' + msg.id} date={msg.date} />;
        }
        return (
          <React.Fragment key={msg.id}>
            {sep}
            {msg.role === 'typing'
              ? <TypingBulle />
              : <Bulle msg={msg} nomAssistant={nomAssistant} />
            }
          </React.Fragment>
        );
      })}
      <div ref={bas} />
    </div>
  );
}

/* ── Styles ── */
const styles = {
  fond: {
    flex: 1,
    overflowY: 'auto',
    padding: '.8rem .85rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '.3rem',
    background: '#ece5dd',
    backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,.035) 1px, transparent 0)',
    backgroundSize: '20px 20px',
  },
  row: { display: 'flex', flexDirection: 'column', animation: 'popIn .18s ease forwards' },
  rowUser: { alignItems: 'flex-end' },
  rowBot:  { alignItems: 'flex-start' },
  auteur: { fontSize: '.7rem', fontWeight: 600, color: '#1a3a5c', marginBottom: '.15rem', paddingLeft: '.2rem' },
  bulle: { maxWidth: '78%', padding: '.6rem .85rem .45rem', borderRadius: 12, fontSize: '.88rem', lineHeight: 1.55, wordBreak: 'break-word', position: 'relative' },
  bulleUser: { background: '#dcf8c6', borderRadius: '12px 2px 12px 12px', color: '#111' },
  bulleBot:  { background: '#fff', borderRadius: '2px 12px 12px 12px', color: '#111', boxShadow: '0 1px 2px rgba(0,0,0,.1)' },
  texte: { lineHeight: 1.55 },
  heure: { fontSize: '.63rem', color: 'rgba(0,0,0,.4)', textAlign: 'right', marginTop: '.25rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '.25rem' },
  heureUser: {},
  coches: { color: '#53bdeb', fontSize: '.75rem' },
  pjListe: { display: 'flex', flexDirection: 'column', gap: '.3rem', marginTop: '.4rem' },
  pjItem: { display: 'flex', alignItems: 'center', gap: '.5rem', background: 'rgba(0,0,0,.05)', borderRadius: 8, padding: '.4rem .6rem', fontSize: '.78rem' },
  pjNom: { fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 },
  pjSz: { color: '#888', fontSize: '.68rem', flexShrink: 0 },
  btnDoc: {
    display: 'inline-flex', alignItems: 'center', gap: '.45rem',
    marginTop: '.5rem', padding: '.45rem .9rem',
    background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 8,
    fontSize: '.8rem', fontWeight: 600, fontFamily: 'Inter, sans-serif', cursor: 'pointer',
  },
  typing: { display: 'flex', alignItems: 'center', gap: 5, padding: '.65rem .85rem', background: '#fff', borderRadius: '2px 12px 12px 12px', width: 58, boxShadow: '0 1px 2px rgba(0,0,0,.1)' },
  dot: {
    display: 'inline-block',
    width: 8, height: 8, borderRadius: '50%', background: '#aaa',
    animation: 'tdot 1.3s ease infinite',
  },
  dateSep: { textAlign: 'center', margin: '.6rem 0 .3rem' },
  dateSepInner: { background: 'rgba(255,255,255,.75)', color: '#888', fontSize: '.68rem', padding: '.25rem .7rem', borderRadius: 8 },
  vide: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '.5rem', padding: '2rem 1rem', textAlign: 'center', margin: 'auto' },
  videEmoji: { fontSize: '2.5rem', opacity: .5 },
  videTitre: { fontWeight: 600, color: '#888', fontSize: '.9rem' },
  videSous: { fontSize: '.78rem', color: '#bbb', maxWidth: 260 },
};
